import OptionsMeatball from 'components/Icons/OptionsMeatball';
import ConfirmModal from 'components/modals/ConfirmModal';
import ManageConnectionModal from 'components/modals/ManageConnectionModal';
import OutsideClick from 'components/OutsideClick';
import AppStoreContext from 'contexts/AppStoreContext';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import React, { useState, useContext } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { ModalState } from 'records/ModalState';
import { truncateString } from 'utils/general';
import { useTranslator } from '@bluebeam/react-i18n';

interface Props {
  projectId: string;
  providerType: string;
  connectionId: string;
  accountLabel: string;
  isExpired: boolean;
}

const ManageConnectionDropdown: React.FC<Props> = ({
  projectId = '',
  connectionId = '',
  accountLabel = '',
  isExpired = false,
  providerType,
}) => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const [open, setOpen] = useState<boolean>(false);

  const { actions } = useContext(AppStoreContext);
  const { translate } = useTranslator([
    'integrationsPage',
    'buttons',
    'dropdownOptions',
  ]);

  const toggleDropdown = (evt: React.MouseEvent<HTMLElement>): void => {
    evt.stopPropagation();
    setOpen(!open);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleManageConnection = (evt: React.MouseEvent<HTMLElement>): void => {
    evt.stopPropagation();
    if (isPendoEnabled) {
      pendo.track('Manage connection initiated');
    }
    const modalState = new ModalState({
      className: 'modal__window modal--sm',
      id: 'launchManageConnectionModal',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        providerType,
        connectionId,
        accountLabel: truncateString(accountLabel, 40),
        isExpired,
        newConnection: false,
      },
      component: ManageConnectionModal,
    });

    actions.createModal(modalState);
    setOpen(false);
  };

  const handleConfirmDisconnectAccount = async (
    connectionId: string,
    projectId: string
  ): Promise<void> => {
    await actions.disconnectAccount(connectionId, projectId);
    actions.clearAllModals();
  };

  const handleDisconnectClick = (evt: React.MouseEvent<HTMLElement>): void => {
    evt.stopPropagation();

    const modalState = new ModalState({
      id: 'launchDisconnectAccountDialog',
      portal: 'main',
      open: true,
      overlayClassName: 'modal__overlay',
      props: {
        headerText: translate(
          'integrationsPage:disconnectAccountDialog.headerText'
        ),
        acceptButtonText: translate('buttons:delete'),
        denyButtonText: translate('buttons:cancel'),
        acceptButtonType: 'delete',
        content: (
          <div className="column">
            <div className="bold pt1 mb3">
              {translate(
                'integrationsPage:disconnectAccountDialog.dialogQuestion'
              )}
            </div>
            <div>
              {translate('integrationsPage:disconnectAccountDialog.bodyText')}
            </div>
          </div>
        ),
        denyCallback: (): void => {
          actions.clearAllModals();
        },
        acceptCallback: (): void => {
          if (isPendoEnabled) {
            pendo.track('Integration disconnected');
          }
          handleConfirmDisconnectAccount(connectionId, projectId);
        },
      },
      component: ConfirmModal,
    });

    actions.createModal(modalState);
    setOpen(false);
  };

  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <div ref={ref} className="hs2 flex items-center">
            <div
              id="manage-integration-modal-dropdown"
              className="row items-center cursor-pointer wrem-1p75"
              onClick={toggleDropdown}
            >
              <div className="font-size-8 text-align-center vertical-align-middle pb2">
                <OptionsMeatball />
              </div>
            </div>
          </div>
        )}
      </Reference>
      <Popper placement="bottom-end">
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              className="bgd-white wrem-10 b-2"
              style={{
                ...style,
                zIndex: 2,
              }}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div
                  className="column items-center color-primary-dark font-size-4"
                  style={{ width: '12rem' }}
                >
                  <div className="bb-2 w100 flex-center py1">
                    <div
                      className="m2 py1 cursor-pointer"
                      onClick={handleManageConnection}
                    >
                      {translate('dropdownOptions:manageConnection')}
                    </div>
                  </div>
                  <div className="w100 bb-2 flex-center cursor-pointer py1">
                    <div
                      className="m2 py1 cursor-pointer color-error"
                      onClick={handleDisconnectClick}
                    >
                      {translate('dropdownOptions:disconnectAccount')}
                    </div>
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default ManageConnectionDropdown;
